"use strict";
// P5 W5 W7 T1 — Queue customer-service contracts.
//
// Schemas for QueueCounter, QueueWebhookSecret (summary only — never exposes
// the secret plaintext after creation), QueueEvent, and the webhook ingress
// body. Also includes the server-fanned-out WebSocket event shape.
Object.defineProperty(exports, "__esModule", { value: true });
exports.QueueTicketCalledEvent = exports.QueueTelemetryBody = exports.PlaybackRenderTelemetry = exports.QueueRenderTelemetry = exports.WebhookEventBody = exports.QueueEventRow = exports.QueueWebhookSecretWithPlaintext = exports.QueueWebhookSecretSummary = exports.UpdateQueueCounterInput = exports.CreateQueueCounterInput = exports.QueueCounterRow = exports.I18nText = void 0;
const zod_1 = require("zod");
exports.I18nText = zod_1.z.object({
    fr: zod_1.z.string(),
    en: zod_1.z.string(),
});
// ─── QueueCounter ────────────────────────────────────────────────────────────
exports.QueueCounterRow = zod_1.z.object({
    id: zod_1.z.string().uuid(),
    tenant_id: zod_1.z.string().uuid(),
    group_id: zod_1.z.string().uuid(),
    counter_id_external: zod_1.z.string().min(1),
    counter_label_i18n: exports.I18nText,
    created_at: zod_1.z.string().datetime(),
    updated_at: zod_1.z.string().datetime(),
});
exports.CreateQueueCounterInput = zod_1.z.object({
    counter_id_external: zod_1.z.string().min(1).max(64),
    group_id: zod_1.z.string().uuid(),
    counter_label_i18n: exports.I18nText,
});
exports.UpdateQueueCounterInput = exports.CreateQueueCounterInput.partial();
// ─── QueueWebhookSecret ──────────────────────────────────────────────────────
// The actual secret is never re-exposed after creation.
// QueueWebhookSecretWithPlaintext is returned ONCE on creation only.
exports.QueueWebhookSecretSummary = zod_1.z.object({
    id: zod_1.z.string().uuid(),
    tenant_id: zod_1.z.string().uuid(),
    algorithm: zod_1.z.literal('hmac_sha256'),
    created_by_id: zod_1.z.string().uuid(),
    created_at: zod_1.z.string().datetime(),
    revoked_at: zod_1.z.string().datetime().nullable(),
});
// Secret returned ONCE on creation (plaintext); never re-returned after that.
exports.QueueWebhookSecretWithPlaintext = exports.QueueWebhookSecretSummary.extend({
    plaintext: zod_1.z.string().min(1),
});
// ─── QueueEvent ──────────────────────────────────────────────────────────────
// raw_payload is omitted from the row summary (kept server-side for forensics).
exports.QueueEventRow = zod_1.z.object({
    id: zod_1.z.string().uuid(),
    tenant_id: zod_1.z.string().uuid(),
    counter_id: zod_1.z.string().uuid(),
    webhook_secret_id: zod_1.z.string().uuid(),
    ticket_number: zod_1.z.string().min(1),
    served_at: zod_1.z.string().datetime(),
    received_at: zod_1.z.string().datetime(),
    fanout_player_count: zod_1.z.number().int().min(0),
});
// ─── Webhook ingress body ─────────────────────────────────────────────────────
// What the upstream queue system POSTs to POST /api/v1/queue-webhook/:tenant_id.
// counter_id is the external string (not a UUID) — e.g. "N3".
exports.WebhookEventBody = zod_1.z.object({
    counter_id: zod_1.z.string().min(1).max(64),
    ticket_number: zod_1.z.string().min(1).max(64),
    served_at: zod_1.z.string().datetime(),
});
// ─── PWA queue-render telemetry (W7 T9) ──────────────────────────────────────
// Posted by the PWA player to POST /api/v1/players/me/queue-telemetry when the
// queue overlay starts rendering. Server computes end-to-end latency as
//   latency_seconds = (client_received_at - served_at) / 1000
// using server-anchored time (Phase 1 §5): client_received_at = Date.now() +
// serverTimeOffsetMs so the timestamp is on the same reference as served_at.
//
// NOTE: tenant_id is unbounded user-controlled cardinality in the histogram
// label set. This is intentional per spec §7 and operators must size their
// Prometheus cardinality budget accordingly.
exports.QueueRenderTelemetry = zod_1.z.object({
    /** Composite event id: "<tenant_id>.<ticket_number>.<served_at>" */
    event_id: zod_1.z.string().min(1),
    /** Tenant for histogram label cardinality. */
    tenant_id: zod_1.z.string().uuid(),
    /** ISO-8601 timestamp from the original webhook event (server-side origin). */
    served_at: zod_1.z.string().datetime(),
    /**
     * ISO-8601 timestamp recorded on the player when the overlay started
     * rendering. Uses server-anchored time: Date.now() + serverTimeOffsetMs.
     */
    client_received_at: zod_1.z.string().datetime(),
    /** How long (ms) the browser took to render — informational, not used for histogram. */
    client_render_ms: zod_1.z.number().int().min(0).optional(),
});
// ─── PWA player-side overlay render-time telemetry (P5 §7.3) ────────────────
// Posted by the PWA player to POST /api/v1/players/me/queue-telemetry when the
// queue overlay finishes rendering (duration_ms = renderEnd - renderStart via
// performance.now()). The server uses this to populate the
// displaycms_queue_overlay_render_ms histogram labeled by tenant_id (derived
// server-side from the device JWT — not present in the wire body).
//
// Discriminated from QueueRenderTelemetry by type='playback.render'.
// tenant_id is intentionally absent from the wire body; the server derives it
// from the authenticated device JWT to avoid user-controlled label cardinality.
exports.PlaybackRenderTelemetry = zod_1.z.object({
    /** Discriminator — must equal 'playback.render'. */
    type: zod_1.z.literal('playback.render'),
    /** Composite event id: "<event_id>" from the originating queue ticket. */
    event_id: zod_1.z.string().min(1),
    /** Render duration in ms (performance.now() delta). */
    duration_ms: zod_1.z.number().int().min(0),
    /**
     * ISO-8601 timestamp when rendering completed.
     * Uses server-anchored time: Date.now() + serverTimeOffsetMs.
     */
    rendered_at: zod_1.z.string().datetime(),
});
// ─── Queue-telemetry endpoint union (POST /api/v1/players/me/queue-telemetry) ─
// Existing W7 T9 bodies (QueueRenderTelemetry) have no `type` field.
// New P5 §7.3 bodies (PlaybackRenderTelemetry) carry `type='playback.render'`.
// The union tries PlaybackRenderTelemetry first (has a required literal
// discriminator), then falls back to QueueRenderTelemetry.
exports.QueueTelemetryBody = zod_1.z.union([
    exports.PlaybackRenderTelemetry,
    exports.QueueRenderTelemetry,
]);
// ─── Server-fanned-out WebSocket event ───────────────────────────────────────
// Shape emitted by the /ws/queue namespace to subscribed PWA players.
// expires_at = served_at + 20s overlay duration (server-computed).
// voice_chime is a content-addressed audio resource filename or null.
exports.QueueTicketCalledEvent = zod_1.z.object({
    type: zod_1.z.literal('queue.ticket.called'),
    tenant_id: zod_1.z.string().uuid(),
    counter_id: zod_1.z.string().min(1),
    counter_label_i18n: exports.I18nText,
    ticket_number: zod_1.z.string().min(1),
    served_at: zod_1.z.string().datetime(),
    expires_at: zod_1.z.string().datetime(),
    voice_chime: zod_1.z.string().nullable(),
});
//# sourceMappingURL=queue.schema.js.map